import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import {
  CertificateSaleDetailsResponse,
  CertificateSalesFilters,
  CertificateSalesResponse,
  CalculateCertificateSalePriceRequest,
  CalculateCertificateSalePriceResult,
  CreateCertificateSaleRequest,
  PartnerCertificateSaleResult,
} from '@app/core/models';

const EMPTY_CERTIFICATE_SALES_PAGE: CertificateSalesResponse = {
  currentPageNumber: 0,
  pageSize: 10,
  totalElements: 0,
  totalPages: 0,
  isLastPage: true,
  isFirstPage: true,
  isEmpty: true,
  content: [],
};

@Injectable({
  providedIn: 'root',
})
export class CertificateSaleService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);
  readonly isSaving = signal(false);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/b2b/certificates`;
  }

  getAdminPage(
    page = 0,
    size = 10,
    filters: CertificateSalesFilters = {}
  ): Observable<CertificateSalesResponse> {
    this.isLoading.set(true);

    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    params = this.appendParam(params, 'query', filters.query);
    params = this.appendParam(params, 'certificateNumber', filters.certificateNumber);
    params = this.appendParam(params, 'phoneNumber', filters.phoneNumber);
    params = this.appendParam(params, 'partnerId', filters.partnerId);
    params = this.appendParam(params, 'networkId', filters.networkId);
    params = this.appendParam(params, 'storeId', filters.storeId);
    params = this.appendParam(params, 'startDate', filters.startDate);
    params = this.appendParam(params, 'endDate', filters.endDate);

    for (const status of filters.statuses ?? []) {
      const value = status.trim();
      if (value) params = params.append('statuses', value);
    }

    return this.http.get<CertificateSalesResponse>(`${this.baseUrl}/admin`, { params }).pipe(
      catchError(error => {
        console.error('Ошибка при получении продаж:', error);
        this.translate.get('components.management.sales.errors.load').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of({ ...EMPTY_CERTIFICATE_SALES_PAGE, pageSize: size });
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  /** Создать оплаченную и активированную продажу (у агента — от имени работодателя и его магазина) */
  createPartnerSale(body: CreateCertificateSaleRequest): Observable<PartnerCertificateSaleResult> {
    this.isSaving.set(true);

    return this.http.post<PartnerCertificateSaleResult>(`${this.baseUrl}/partner-sale`, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании продажи:', error);
        this.translate.get(this.getCreateErrorKey(error)).subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isSaving.set(false))
    );
  }

  calculateSalePrice(body: CalculateCertificateSalePriceRequest): Observable<CalculateCertificateSalePriceResult | null> {
    return this.http.post<CalculateCertificateSalePriceResult>(`${this.baseUrl}/calculate-sale-price`, body).pipe(
      catchError(error => {
        console.error('Ошибка при расчете стоимости сертификата:', error);
        this.translate.get(this.getCalculateErrorKey(error)).subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of(null);
      })
    );
  }

  getAdminDetails(certificateId: string): Observable<CertificateSaleDetailsResponse | null> {
    if (!certificateId) return of(null);
    this.isLoading.set(true);

    return this.http.get<CertificateSaleDetailsResponse>(`${this.baseUrl}/admin/${certificateId}`).pipe(
      catchError(error => {
        console.error('Ошибка при получении деталей продажи:', error);
        this.translate.get('components.management.sales.errors.load').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of(null);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  private appendParam(params: HttpParams, key: string, value?: string): HttpParams {
    const normalized = value?.trim();
    return normalized ? params.set(key, normalized) : params;
  }

  private getCreateErrorKey(error: unknown): string {
    const message = this.getBackendMessage(error);

    if (message === 'No active rule found for selected network and selected series/package') {
      return 'components.management.sales.errors.noActiveRule';
    }

    if (message === 'No matching sale range found for device price') {
      return 'components.management.sales.errors.noMatchingSaleRange';
    }

    return 'components.management.sales.errors.create';
  }

  private getCalculateErrorKey(error: unknown): string {
    const message = this.getBackendMessage(error);

    if (message === 'No active rule found for selected network and selected series/package') {
      return 'components.management.sales.errors.noActiveRule';
    }

    if (message === 'No matching sale range found for device price') {
      return 'components.management.sales.errors.noMatchingSaleRange';
    }

    return 'components.management.sales.errors.calculate';
  }

  private getBackendMessage(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) return '';

    const apiError = error.error as unknown;
    if (apiError && typeof apiError === 'object' && 'message' in apiError) {
      const message = (apiError as { message?: unknown }).message;
      if (typeof message === 'string') return message.trim();
    }

    return '';
  }
}
